import { useState, useEffect } from 'react';
import {
  Bell,
  Settings,
} from 'lucide-react';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationItem } from './NotificationItem';
import {
  NotificationType,
  NotificationTypeConfig,
  UserNotification,
} from '@/types/notification.types';
import { notificationService } from '@/services/notification.service';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import Pagination from '@/components/ui/Pagination';

interface NotificationCenterProps {
  onOpenPreferences?: () => void;
  isLearner?: boolean;
  // When true, hide the inner "Notifications" title row so the component
  // fits cleanly inside an outer tab whose label already names the section.
  embedded?: boolean;
}

type ChannelFilterTab = 'ALL' | 'IN_APP' | 'EMAIL' | 'PUSH';

// Build select options for admin filters
const typeOptions: SelectOption[] = [
  { label: 'All types', value: 'ALL' },
  ...Object.entries(NotificationTypeConfig).map(([type, config]) => ({
    label: config.label,
    value: type,
  })),
];


export function NotificationCenter({
  onOpenPreferences,
  isLearner = false,
  embedded = false,
}: NotificationCenterProps) {
  const {
    markAsRead,
  } = useNotifications();

  // Local state — independent from the shared store so polling doesn't overwrite pagination
  const [notifications, setLocalNotifications] = useState<UserNotification[]>([]);
  const [totalCount, setLocalTotalCount] = useState(0);
  const [isLoading, setLocalIsLoading] = useState(true);

  // Channel filter tabs (shared between learner and admin My Notifications)
  const [channelTab, setChannelTab] = useState<ChannelFilterTab>('ALL');

  // Admin filters - type filter triggers backend API calls
  const [typeFilter, setTypeFilter] = useState<string>('ALL');

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter]);

  // Pagination — backend-driven
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const totalPages = Math.ceil(totalCount / pageSize);

  const is_admin = !isLearner;

  // Fetch from backend when page, pageSize, or filters change.
  useEffect(() => {
    const fetchPage = async () => {
      setLocalIsLoading(true);

      const channel = channelTab !== 'ALL' ? channelTab : undefined;

      const params = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        channel,
        ...(!isLearner && typeFilter !== 'ALL' ? { type: typeFilter as NotificationType } : {}),
      };

      const response = await notificationService.getNotifications(params);

      if (response.success) {
        setLocalNotifications(response.data);
        setLocalTotalCount(response.meta_data?.total ?? response.data.length);
      }
      setLocalIsLoading(false);
    };

    fetchPage();
  }, [currentPage, pageSize, typeFilter, channelTab, isLearner]);

  const hasActiveAdminFilters = typeFilter !== 'ALL';
  const hasChannelFilter = channelTab !== 'ALL';

  const clearAdminFilters = () => {
    setTypeFilter('ALL');
    setCurrentPage(1);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 z-10">
        <div className="flex items-center justify-between">
          {!embedded ? (
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-xl">
                <Bell className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Notifications
                </h2>
                <p className="text-sm text-gray-500">Your notifications</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Your notifications</p>
          )}

          <div className="flex items-center gap-3">
            {/* Admin: SimpleSelect filters inline */}
            {!isLearner && (
              <>
                <div className="w-44">
                  <SimpleSelect
                    options={typeOptions}
                    value={typeFilter}
                    onChange={(val) => setTypeFilter((val as string) || 'ALL')}
                    placeholder="All types"
                    isSearchable={false}
                    isClearable={false}
                  />
                </div>
                {hasActiveAdminFilters && (
                  <button
                    onClick={clearAdminFilters}
                    className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
                  >
                    Clear
                  </button>
                )}
              </>
            )}

            {/* Settings button - admin only */}
            {!isLearner && onOpenPreferences && (
              <button
                onClick={onOpenPreferences}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Notification settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Channel filter pill tabs (All | In-App | Email | Push) */}
        {isLearner && (
          <div className="flex items-center gap-2 mt-4">
            {(['ALL', 'IN_APP', 'EMAIL', 'PUSH'] as ChannelFilterTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => { setChannelTab(tab); setCurrentPage(1); }}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                  channelTab === tab
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab === 'ALL' ? 'All' : tab === 'IN_APP' ? 'In-App' : tab === 'EMAIL' ? 'Email' : 'Push'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Notifications list */}
      <div className="divide-y divide-gray-100">
        {isLoading && notifications.length === 0 ? (
          <div className="p-4 space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-start gap-4">
                <SkeletonLoader type="avatar" className="flex-shrink-0 w-12 h-12" />
                <div className="flex-1 space-y-2">
                  <SkeletonLoader type="text" className="h-3 w-20" />
                  <SkeletonLoader type="text" className="h-4 w-3/4" />
                  <SkeletonLoader type="text" className="h-3 w-1/2" />
                  <SkeletonLoader type="text" className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Bell className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {isLearner
                ? hasChannelFilter
                  ? `No ${channelTab === 'IN_APP' ? 'in-app' : channelTab === 'EMAIL' ? 'email' : 'push'} notifications`
                  : 'No notifications yet'
                : hasActiveAdminFilters
                ? 'No matching notifications'
                : 'No notifications'
              }
            </h3>
            <p className="text-gray-500 text-center max-w-sm">
              {isLearner
                ? hasChannelFilter
                  ? 'Switch to "All" to see all notifications.'
                  : "We'll notify you when something new arrives."
                : hasActiveAdminFilters
                ? 'Try adjusting your filters to see more results'
                : "You're all caught up! We'll notify you when something new arrives."
              }
            </p>
            {!isLearner && hasActiveAdminFilters && (
              <button
                onClick={clearAdminFilters}
                className="mt-4 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onMarkAsRead={markAsRead}
                showActions={!isLearner}
              />
            ))}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalCount > 0 && totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalCount}
          itemsPerPage={pageSize}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}
    </div>
  );
}

export default NotificationCenter;
